<template>
    <modal ref="modal" :id="modalId" size="lg" :show-close="failed" :close="close">
        <div v-if="reservingAppointment || cancellingReservedAppointment" class="pt-20 pb-16 pl-4 text-center">
            <div class="loader"></div>
        </div>

        <div v-else-if="failed" class="py-16 text-center">
            <h1 class="page-title">Appointment unavailable</h1>
            <p>{{ failedMessage }}</p>
        </div>

        <div v-else-if="expired">
            <div class="text-center">
                <h1 class="page-title">Reservation expired</h1>
            </div>

            <div class="text-center mt-4 mb-8 md:mb-16">
                <p>Your reservation for this slot has expired. Please go back and choose another.</p>
            </div>

            <div class="modal-footer text-center">
                <button type="button" role="button" class="btn btn-secondary" @click="$refs.modal.closeModal">Go back</button>
            </div>
        </div>

        <div v-else>
            <div class="text-center">
                <h1 class="page-title">Appointment selected</h1>
            </div>

            <div class="reservation">
                <div class="reserved-datetime md:w-1/2">
                    <img src="../../assets/images/calendar.svg" alt="" />

                    <div class="reserved-date">
                        {{ dateTime.format('dddd Do MMMM') }}
                    </div>

                    <div class="reserved-time">
                        {{ dateTime.format('HH:mm') }}
                    </div>
                </div>

                <div v-if="clinic.ClinicID == videoClinicID"  class="reserved-clinic-price md:w-1/4">
                    <div class="text-2xl">{{ videoPrice }}</div>
                    <div class="">
                        <img src="../../assets/images/lock.svg" alt="Lock icon" />
                        <span class="pl-2 text-sm">Secure online checkout</span>
                    </div>
                </div>

                <div v-else class="reserved-clinic md:w-1/4">
                    <img src="../../assets/images/map-marker-blue.svg" alt="" />

                    <div class="reserved-clinic-name">
                        {{ clinic.ClinicName }}
                    </div>

                    <div class="reserved-clinic-info">
                        {{ clinic.Postcode }} &nbsp; {{ clinic.Distance }} miles
                    </div>
                </div>

                <div class="reserved-therapist md:w-1/4">
                    <div class="reserved-clinician-name">
                        {{ therapist.Name }}
                    </div>

                    <div class="reserved-clinician-info">
                        Clinician
                    </div>
                </div>
            </div>

            <div class="text-center mb-8 mt-6 md:mt-0 md:mb-16">
                <p>
                    Reserved for another
                    <timer v-if="appointmentData" @expired="expired = true" />
                    minutes.
                </p>
            </div>

            <div class="modal-footer text-center">
                <button type="button"
                        role="button"
                        class="btn btn-primary"
                        @click="$router.push({ name: 'booking' })"
                >Book now</button>

                <div class="text-sm mt-2">
                    <a href="#" @click.prevent="sendCancelReservedAppointmentRequest">
                        Change my choice
                    </a>
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
    import { mapActions, mapState } from 'vuex';

    import DateTime from '../../lib/DateTime';
    import Timer from './Timer';
    import { Prices } from '../../lib/Prices';

    export default {
        name: 'reserve-appointment',

        components: {
            Timer,
        },

        props: {
            clinic: {
                type: Object,
                required: true,
            },

            therapist: {
                type: Object,
                required: true,
            },

            date: {
                type: String,
                required: true,
            },

            time: {
                type: String,
                required: true,
            },

            length: {
                type: Number,
                required: true,
            },

            modalId: {
                type: String,
                required: true,
            },

            close: Function,
        },

        data() {
            return {
                failed: false,
                failedMessage: null,
                expired: false,
            };
        },

        computed: {
            ...mapState('appointment', [
                'appointmentData',
            ]),

            ...mapState('appointments', [
                'reservingAppointment',
                'cancellingReservedAppointment',
            ]),

            dateTime() {
                return DateTime.merge(this.date, this.time);
            },

            videoClinicID() {
                return process.env.VUE_APP_VIDEO_CLINIC_ID;
            },

            videoPrice() {
                return `£${Prices.video()}`;
            }
        },

        mounted() {
            this.listen('appointment.reserve.error', this.reserveFailed);

            this.sendReserveAppointmentRequest();

            this.showModal(this.modalId);
        },

        destroyed() {
            this.silence('appointment.reserve.error', this.reserveFailed);
        },

        methods: {
            ...mapActions('appointments', [
                'reserveAppointment',
                'cancelReservedAppointment',
            ]),

            sendReserveAppointmentRequest() {
                this.failed = false;
                this.failedMessage = null;

                const params = {
                    clinic: {
                        ClinicID: this.clinic.ClinicID,
                        ClinicName: this.clinic.ClinicName,
                        Address1: this.clinic.Address1,
                        Postcode: this.clinic.Postcode,
                        InsideM25: this.clinic.InsideM25,
                        ClinicTier: this.clinic.ClinicTier,
                        Distance: this.clinic.Distance,
                    },
                    therapist: {
                        TherapistID: this.therapist.TherapistID,
                        Name: this.therapist.Name,
                        Gender: this.therapist.Gender,
                    },
                    appointment: {
                        Date: this.date,
                        Time: this.time,
                        AppointmentLength: this.length,
                    },
                };

                const callback = (data) => {
                    if (data.AppointmentID) {
                        this.$gtm.trackView('Appointment Selected', '/search/appointment');
                    }
                };

                this.reserveAppointment({ params, callback });
            },

            reserveFailed(e) {
                this.failed = true;
                this.failedMessage = e.message || 'Sorry, there was a problem reserving this appointment slot.';
            },

            sendCancelReservedAppointmentRequest() {
                const appointmentId = this.appointmentData.appointment.AppointmentID;

                const callback = () => this.$refs.modal.closeModal();

                this.cancelReservedAppointment({ appointmentId, callback });
            },
        },
    }
</script>
